import React, { useState, useEffect, useMemo } from 'react';
import DynamicForm from '../../components/forms/DynamicForm';
import { USER_FIELDS } from '../../models/userFields';

/**
 * Modal para editar datos del usuario
 * Usa DynamicForm para el renderizado y manejo de campos
 */
export default function UserDataModal({ usuario, onClose, onSave }) {
  const [recordData, setRecordData] = useState({});

  // Preparar datos del usuario para el formulario
  useEffect(() => {
    if (!usuario) return;
    
    const prepared = {};
    
    Object.keys(USER_FIELDS).forEach((key) => {
      let value = usuario[key];
      
      // Manejo especial para campos de ubicación
      if (key === 'ubicacionCiudad') {
        value = value || usuario.ubicacion?.ciudad || '';
      } else if (key === 'ubicacionEstado') {
        value = value || usuario.ubicacion?.estado || '';
      } else if (key === 'ubicacionPais') {
        value = value || usuario.ubicacion?.pais || '';
      }
      
      // Usar el valor default si no existe
      if (value === undefined || value === null) {
        const fieldDef = USER_FIELDS[key];
        value = fieldDef?.default ?? '';
      }
      
      prepared[key] = value;
    });
    
    setRecordData(prepared);
  }, [usuario]);

  // Transformar USER_FIELDS a array y filtrar solo campos visibles
  const fieldsArray = useMemo(() => {
    return Object.keys(USER_FIELDS)
      .filter(fieldKey => {
        const field = USER_FIELDS[fieldKey];
        // Solo mostrar campos que tengan "userEdicion" en visibleIn
        return field.visibleIn && field.visibleIn.includes('userEdicion');
      })
      .map(fieldKey => ({
        ...USER_FIELDS[fieldKey],
        fieldKey: fieldKey  // DynamicForm usa 'fieldKey'
      }))
      .sort((a, b) => {
        // Ordenar por orderList
        const orderA = a.orderList || 999;
        const orderB = b.orderList || 999;
        return orderA - orderB;
      });
  }, []);

  // Manejar cambios en el formulario
  const handleChange = (fieldKey, value) => {
    setRecordData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Manejar submit del formulario
  const handleSubmit = async (formData) => {
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error guardando datos:', error);
      throw error; // DynamicForm manejará el error
    }
  };

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content" style={{ borderRadius: 'var(--radius-lg)' }}>
          {/* Header */}
          <div className="modal-header" style={{ borderBottom: '1px solid var(--gray-200)' }}>
            <h5 className="modal-title" style={{ 
              fontSize: 'var(--font-size-xl)', 
              fontWeight: 'var(--font-weight-bold)' 
            }}>
              Mis datos
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Cerrar"
            ></button>
          </div>
          
          {/* Body con DynamicForm */}
          <div className="modal-body" style={{ padding: 'var(--space-4)' }}>
            <DynamicForm
              fields={fieldsArray}
              recordData={recordData}
              view="userEdicion"
              onChange={handleChange}
              onSubmit={handleSubmit}
              readOnly={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}